import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { TutorialVideoDialogComponent } from '../tutorial-video-dialog/tutorial-video-dialog.component';
import {
  SERASA_CASE_CODE,
  TUTORIAL_DIALOG_CONFIG,
} from '../tutorial-video.constants';
import { TutorialVideoService } from '../tutorial-video.service';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'app-tutorial-video-fab',
  templateUrl: './tutorial-video-fab.component.html',
  styleUrls: ['./tutorial-video-fab.component.scss'],
})
export class TutorialVideoFabComponent implements OnInit, OnDestroy {
  showFab = false;
  dialogOpen = false;

  private readonly destroy$ = new Subject<void>();
  private visibilityRequestId = 0;

  constructor(
    private readonly claimService: ClaimsService,
    private readonly dialog: MatDialog,
    private readonly videoService: TutorialVideoService,
    private readonly router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      await this.updateFabVisibility();

      this.router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          takeUntil(this.destroy$)
        )
        .subscribe(() => void this.updateFabVisibility());
    } catch {
      this.showFab = false;
    }

    this.videoService.pipClosedByUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.openDialog());
  }
  
  private async updateFabVisibility(): Promise<void> {
    const requestId = ++this.visibilityRequestId;
    const tree = this.router.parseUrl(this.router.url);
    const segments = tree.root.children['primary']?.segments.map(s => s.path) ?? [];

    const isSerasaCaseRoute =
      segments[0] === 'client' &&
      segments[1] === 'cases' &&
      segments[2] === SERASA_CASE_CODE;

    if (isSerasaCaseRoute) {
      this.showFab = true;
      return;
    }

    const isClaimRoute =
      segments[0] === 'client' &&
      segments[1] === 'claim' &&
      !!segments[2];

    if (!isClaimRoute) {
      this.showFab = false;
      return;
    }

    try {
      const claim = await this.claimService.getClaimWithCase(segments[2]);
      if (requestId !== this.visibilityRequestId) return;

      this.showFab = claim?.cp_case?.code === SERASA_CASE_CODE;
    } catch {
      if (requestId === this.visibilityRequestId) {
        this.showFab = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async openTutorial(): Promise<void> {
    if (this.videoService.isInPip) {
      await this.videoService.exitPip();
    }
    this.openDialog();
  }

  private openDialog(): void {
    const alreadyOpen = this.dialog.openDialogs.some(
      (d) => d.componentInstance instanceof TutorialVideoDialogComponent
    );
    if (!alreadyOpen) {
      const ref = this.dialog.open(TutorialVideoDialogComponent, TUTORIAL_DIALOG_CONFIG);
      this.dialogOpen = true;
      ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.dialogOpen = false;
      });
    }
  }
}
